import { ColorPickerV2 } from '@bynder/design-system';
import type { Color, ColorStop, Gradient, SolidColor } from 'packages/ds/ColorPicker/types';

export type PickerValue = ColorPickerV2.ColorWithAlpha | ColorPickerV2.Gradient<ColorPickerV2.ColorWithAlpha>;

const FULL_OPACITY = 100;
const HALF_TURN = 180;

function toColorWithAlpha(color: SolidColor): ColorPickerV2.ColorWithAlpha {
    return typeof color === 'string' ? { color, opacity: FULL_OPACITY } : color;
}

function isGradient(value: Color): value is Gradient {
    return typeof value === 'object' && 'type' in value;
}

// The canvas renders stored angles, which sit a half turn from the CSS angles the picker shows.
function turnHalf<T extends Gradient>(gradient: T): T {
    if (gradient.type !== 'linear') {
        return gradient;
    }

    return { ...gradient, angle: ((gradient.angle ?? 0) + HALF_TURN) % 360 };
}

/**
 * `ColorPickerV2` pins one color shape across solids and gradient stops, so a bare hex
 * string has to be widened to `{ color, opacity }` in both positions.
 */
export function toPickerValue(value: Color): PickerValue {
    if (isGradient(value)) {
        // DS types stops as a two-or-more tuple; ours is a plain array.
        return turnHalf({
            ...value,
            stops: value.stops.map((stop: ColorStop) => ({ ...stop, color: toColorWithAlpha(stop.color) })),
        }) as PickerValue;
    }

    return toColorWithAlpha(value);
}

export function fromPickerValue(value: Color): Color {
    return isGradient(value) ? turnHalf(value) : value;
}
